import { Component, EventEmitter, Input, OnInit, OnDestroy, AfterViewInit, Output, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FieldsService } from 'src/app/services/fields.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { LocalizationService } from 'src/app/services/localization.service';
import moment from 'moment';
import flatpickr from 'flatpickr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface WeekYearValue {
  week: string;
  year: string;
}

export interface DateRangeValue {
  start: Date;
  end: Date;
}

export interface WeekYearChangeEvent {
  week: string;
  year: string;
  isDateRangeActive: boolean;
  dateRange?: DateRangeValue;
}

@Component({
  selector: 'app-week-year-selector',
  templateUrl: './week-year-selector.component.html',
  styleUrls: ['./week-year-selector.component.css'],
  host: { 'class': 'week-year-selector-host', 'style': 'display: contents;' }
})
export class WeekYearSelectorComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  // Configuration inputs
  @Input() showDateRange: boolean = false;
  @Input() showNoneOption: boolean = true;
  @Input() disableOnFutureWeek: boolean = false;
  @Input() weekColClass: string = 'col-3';
  @Input() yearColClass: string = 'col-3';
  @Input() dateRangeColClass: string = 'col-4';

  // Value inputs (for initialization from URL params, etc.)
  @Input() initialWeek: string;
  @Input() initialYear: string;
  @Input() initialDateRange: Date[];

  // Outputs
  @Output() selectionChange = new EventEmitter<WeekYearChangeEvent>();
  @Output() weekChange = new EventEmitter<string>();
  @Output() yearChange = new EventEmitter<string>();
  @Output() dateRangeChanged = new EventEmitter<DateRangeValue>();
  @Output() isDisabledChange = new EventEmitter<boolean>();

  // Internal state
  week: string;
  year: string;
  @ViewChild('dateRangeInput') dateRangeInput!: ElementRef;
  dateTimeRange: Date[] = [];
  isDateRangeActive: boolean = false;
  isDisabled: boolean = false;
  disabledTooltip: string = '';
  dateRangeDisplayValue: string = '';
  private dateRangePicker?: flatpickr.Instance;
  private localizationService = new LocalizationService();

  // Dropdown data
  WeeksForYearData: { key: string; value: string }[] = [];
  LastFourYearsData: { key: string; value: string }[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private _fieldsService: FieldsService,
    private _studioConfigService: StudioConfigService
  ) {}

  ngOnInit(): void {
    this.initializeDefaults();
    this.loadDropdownData();
    this.updateDisabledState();
  }

  ngAfterViewInit(): void {
    if (this.showDateRange) {
      this.initDateRangePicker();
    }
  }

  private initDateRangePicker(): void {
    if (!this.dateRangeInput?.nativeElement) return;

    this._studioConfigService.getStudioConfig().pipe(takeUntil(this.destroy$)).subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);

      this.dateRangePicker = flatpickr(this.dateRangeInput.nativeElement, {
        mode: 'range',
        dateFormat: formats.flatpickrDateFormat,
        disableMobile: true,
        defaultDate: this.dateTimeRange?.length === 2 ? this.dateTimeRange : undefined,
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.dateTimeRange = selectedDates;
            this.isDateRangeActive = true;
            this.dateRangeDisplayValue = this.dateRangePicker?.input?.value || '';
            this.dateRangeChanged.emit({
              start: this.dateTimeRange[0],
              end: this.dateTimeRange[1]
            });
            this.emitSelectionChange();
          }
        }
      });

      if (this.dateTimeRange?.length === 2 && this.dateRangePicker) {
        this.dateRangePicker.setDate(this.dateTimeRange, false);
        this.dateRangeDisplayValue = this.dateRangePicker.input.value;
      }
    });
  }

  openDateRangePicker(): void {
    this.dateRangePicker?.open();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle external changes to initial values
    if (changes['initialWeek'] && !changes['initialWeek'].firstChange) {
      this.week = changes['initialWeek'].currentValue;
      this.updateDisabledState();
    }
    if (changes['initialYear'] && !changes['initialYear'].firstChange) {
      this.year = changes['initialYear'].currentValue;
      this.refreshWeeksForYear();
      this.updateDisabledState();
    }
    if (changes['initialDateRange'] && !changes['initialDateRange'].firstChange) {
      const range = changes['initialDateRange'].currentValue;
      if (range && range.length === 2) {
        this.dateTimeRange = range;
        this.isDateRangeActive = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.dateRangePicker?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeDefaults(): void {
    // Set year first (needed for week calculation)
    if (this.initialYear) {
      this.year = this.initialYear;
    } else {
      this.year = moment().weekYear().toString();
    }

    // Set week
    if (this.initialWeek) {
      this.week = this.initialWeek;
    } else {
      this.week = this._fieldsService.getCurrentWeek().toString();
    }

    // Set date range if provided
    if (this.initialDateRange && this.initialDateRange.length === 2) {
      this.dateTimeRange = this.initialDateRange;
      this.isDateRangeActive = true;
    }
  }

  private loadDropdownData(): void {
    this.LastFourYearsData = this._fieldsService.LastFourYearsData();
    this.refreshWeeksForYear();
  }

  private refreshWeeksForYear(): void {
    const yearNum = parseInt(this.year, 10);
    if (!isNaN(yearNum)) {
      this.WeeksForYearData = this._fieldsService.WeeksForYearData(yearNum);
    }
  }

  private updateDisabledState(): void {
    if (!this.disableOnFutureWeek) {
      this.isDisabled = false;
      this.disabledTooltip = '';
      return;
    }

    if (!this.week || !this.year) {
      this.isDisabled = true;
      this.disabledTooltip = 'Select a week and year';
      this.isDisabledChange.emit(this.isDisabled);
      return;
    }

    // Calculate if selected week has ended
    const selectedDate = moment().isoWeekYear(+this.year).isoWeek(+this.week);
    const weekEnd = selectedDate.clone().endOf('isoWeek');
    const now = moment();

    this.isDisabled = !weekEnd.isBefore(now);

    if (this.isDisabled) {
      const weekEndPlusOne = weekEnd.clone().startOf('day').add(1, 'day');
      const daysUntilReady = Math.ceil(weekEndPlusOne.diff(now, 'days', true));

      if (daysUntilReady <= 0) {
        this.disabledTooltip = '';
        this.isDisabled = false;
      } else if (daysUntilReady === 1) {
        this.disabledTooltip = 'Ready tomorrow';
      } else {
        this.disabledTooltip = `Ready in ${daysUntilReady} days`;
      }
    } else {
      this.disabledTooltip = '';
    }

    this.isDisabledChange.emit(this.isDisabled);
  }

  // Event handlers
  onWeekChange(event: any): void {
    this.week = event.value;
    this.updateDisabledState();
    this.weekChange.emit(this.week);
    this.emitSelectionChange();
  }

  onYearChange(event: any): void {
    this.year = event.value;
    this.refreshWeeksForYear();
    this.updateDisabledState();
    this.yearChange.emit(this.year);
    this.emitSelectionChange();
  }


  private emitSelectionChange(): void {
    const event: WeekYearChangeEvent = {
      week: this.week,
      year: this.year,
      isDateRangeActive: this.isDateRangeActive
    };

    if (this.isDateRangeActive && this.dateTimeRange.length === 2) {
      event.dateRange = {
        start: this.dateTimeRange[0],
        end: this.dateTimeRange[1]
      };
    }

    this.selectionChange.emit(event);
  }

  // Public methods for parent components
  clearDateRange(): void {
    this.dateTimeRange = [];
    this.isDateRangeActive = false;
    this.dateRangeDisplayValue = '';
    this.dateRangePicker?.clear();
    this.emitSelectionChange();
  }

  reset(): void {
    this.dateTimeRange = [];
    this.isDateRangeActive = false;
    this.dateRangeDisplayValue = '';
    this.dateRangePicker?.clear();
    this.year = moment().weekYear().toString();
    this.week = this._fieldsService.getCurrentWeek().toString();
    this.refreshWeeksForYear();
    this.updateDisabledState();
    this.emitSelectionChange();
  }

  // Getters for current values
  getCurrentValue(): WeekYearValue {
    return {
      week: this.week,
      year: this.year
    };
  }

  getDateRange(): DateRangeValue | null {
    if (this.isDateRangeActive && this.dateTimeRange.length === 2) {
      return {
        start: this.dateTimeRange[0],
        end: this.dateTimeRange[1]
      };
    }
    return null;
  }
}
